/**
 * API Data Mapper Utility
 * Transforms data from different API sources to a unified format for match widgets
 */

/**
 * Maps live status from new API to expected event status
 * @param {string} liveStatus - Status from new API
 * @returns {string} Mapped status
 */
const mapEventStatus = (liveStatus) => {
  const statusMap = {
    'Live': 'Ongoing',
    'GameEnded': 'Finished',
    'PreGame': 'Scheduled',
    'Postponed': 'Postponed',
    'Cancelled': 'Cancelled',
  };
  
  return statusMap[liveStatus] || 'Scheduled';
};

/**
 * Extracts time string from UTC date
 * @param {string} dateUTC - UTC date string
 * @returns {string} Time in HH:MM format
 */
const extractTime = (dateUTC) => {
  if (!dateUTC) return '';
  
  try {
    const date = new Date(dateUTC);
    return date.toISOString().substring(11, 16); // Extract HH:MM from ISO string
  } catch (error) {
    console.warn('Failed to extract time from date:', dateUTC);
    return '';
  }
};

/**
 * Finds participant by role
 * @param {Array} participants - Array of participants
 * @param {string} role - Role to find ("Home" or "Away")
 * @returns {Object|null} Participant object or null
 */
const findParticipantByRole = (participants, role) => {
  return participants?.find(participant => 
    participant.roles?.includes(role)
  ) || null;
};

/**
 * Simple field mapping with fallback strategies for our two API formats
 * @param {Object} item - Raw match data
 * @param {string} fieldName - Field name to extract
 * @returns {string} Extracted field value
 */
const extractFieldValue = (item, fieldName) => {
  switch (fieldName) {
    case 'gameId':
      return String(item.gameId || item.id || '');
      
    case 'leagueName':
      return item.leagueName || item.roundName || '';
      
    case 'date':
      return item.dateUTC || item.date || '';
      
    case 'time':
      const time = item.time;
      if (time) return time;
      const dateStr = item.dateUTC || item.date;
      return dateStr ? extractTime(dateStr) : '';
      
    case 'arena':
      const venue = item.arena || item.venue;
      return (venue && venue !== '- Ingen arena -') ? venue : '';
      
    case 'homeTeamName':
      if (item.homeTeamName) return item.homeTeamName;
      const homeParticipant = findParticipantByRole(item.participants, 'Home');
      return homeParticipant?.name || '';
      
    case 'guestTeamName':
      if (item.guestTeamName) return item.guestTeamName;
      const awayParticipant = findParticipantByRole(item.participants, 'Away');
      return awayParticipant?.name || '';
      
    case 'homeTeamLogo':
      if (item.homeTeamLogo) return item.homeTeamLogo;
      const homeLogo = findParticipantByRole(item.participants, 'Home');
      return homeLogo?.icon || '';
      
    case 'guestTeamLogo':
      if (item.guestTeamLogo) return item.guestTeamLogo;
      const awayLogo = findParticipantByRole(item.participants, 'Away');
      return awayLogo?.icon || '';
      
      case 'homeScoreResult':
        if (item.homeScoreResult !== undefined && item.homeScoreResult !== null) return String(item.homeScoreResult);
        const homeScore = findParticipantByRole(item.participants, 'Home');
        const score = homeScore?.score?.score;
        return (score !== undefined && score !== null) ? String(score) : null;
        
      case 'guestScoreResult':
        if (item.guestScoreResult !== undefined && item.guestScoreResult !== null) return String(item.guestScoreResult);
        const awayScore = findParticipantByRole(item.participants, 'Away');
        const awayScoreValue = awayScore?.score?.score;
        return (awayScoreValue !== undefined && awayScoreValue !== null) ? String(awayScoreValue) : null;
      
    case 'eventStatus':
      const status = item.eventStatus || item.liveStatus;
      return mapEventStatus(status);
    default:
      return '';
  }
};

/**
 * Transform individual match item
 * @param {Object} item - Raw match data
 * @returns {Object} Transformed match item
 */
const transformMatchItem = (item) => ({
  gameId: extractFieldValue(item, 'gameId'),
  leagueName: extractFieldValue(item, 'leagueName'),
  date: extractFieldValue(item, 'date'),
  time: extractFieldValue(item, 'time'),
  arena: extractFieldValue(item, 'arena'),
  homeTeamName: extractFieldValue(item, 'homeTeamName'),
  homeTeamLogo: extractFieldValue(item, 'homeTeamLogo'),
  homeScoreResult: extractFieldValue(item, 'homeScoreResult'),
  guestTeamName: extractFieldValue(item, 'guestTeamName'),
  guestTeamLogo: extractFieldValue(item, 'guestTeamLogo'),
  guestScoreResult: extractFieldValue(item, 'guestScoreResult'),
  eventStatus: extractFieldValue(item, 'eventStatus')
});

/**
 * Simple data normalizer for legacy and participant-based APIs
 * @param {Object} response - API response
 * @returns {Object} Normalized data structure
 */
export const normalizeMatchData = (response) => {
  try {
    const dataArray = response?.data || response?.result || [];
    
    if (!Array.isArray(dataArray)) {
      console.warn('No valid data array found in response');
      return { data: [] };
    }
    
    const transformedData = dataArray?.map(transformMatchItem)?.filter(item => item.gameId);
    
    // Sort by date to ensure consistent ordering (earliest to latest)
    transformedData.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });
    
    return { data: transformedData };
    
  } catch (error) {
    console.error('Error normalizing match data:', error);
    return { data: [] };
  }
};

/**
 * Builds dynamic URL with date range for new API
 * @param {string} baseUrl - Base API URL
 * @param {Object} dateRange - Date range configuration object with:
 *   - daysBefore: number of days before today (e.g., 120)
 *   - daysAfter: number of days after today (e.g., 30)
 * @returns {string} Complete URL with dynamic dates
 */
export const buildDynamicApiUrl = (baseUrl, dateRange) => {
  if (!dateRange || !baseUrl || typeof dateRange !== 'object') {
    return baseUrl;
  }
  
  const today = new Date();
  const timefrom = new Date(today);
  const timeto = new Date(today);
  
  const daysBefore = Math.abs(dateRange.daysBefore || 0);
  const daysAfter = Math.abs(dateRange.daysAfter || 0);
  
  if (daysBefore === 0 && daysAfter === 0) {
    console.warn('Invalid dateRange: Both daysBefore and daysAfter are 0 or undefined');
    return baseUrl;
  }
  
  timefrom.setDate(today.getDate() - daysBefore);
  timeto.setDate(today.getDate() + daysAfter);
  
  const timeFromISO = timefrom.toISOString();
  const timeToISO = timeto.toISOString();
  
  const url = new URL(baseUrl);
  url.searchParams.delete('timefrom');
  url.searchParams.delete('timeto');
  
  url.searchParams.set('timefrom', timeFromISO);
  url.searchParams.set('timeto', timeToISO);
  
  return url.toString();
};
